
import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from '@/components/ui/label';
import { Clock, Users, Baby, Sun, Mountain } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ActivityDetailsProps {
  place: any;
  isEditing: boolean;
  onInputChange: (field: string, value: any) => void;
}

export function ActivityDetails({ place, isEditing, onInputChange }: ActivityDetailsProps) {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <Mountain className="w-5 h-5" />
        Activity Details
      </h3>

      {isEditing ? (
        <>
          <div className="space-y-2">
            <Label>Difficulty Level</Label>
            <Select
              value={place.difficulty_level || ''}
              onValueChange={(value) => onInputChange('difficulty_level', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select difficulty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="moderate">Moderate</SelectItem>
                <SelectItem value="challenging">Challenging</SelectItem>
                <SelectItem value="expert">Expert</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Duration</Label>
            <Input
              value={place.duration || ''}
              onChange={(e) => onInputChange('duration', e.target.value)}
              placeholder="e.g., 2 hours, Half day"
            />
          </div>

          <div className="space-y-2">
            <Label>Equipment Needed</Label>
            <Input
              value={place.equipment_needed?.join(', ') || ''}
              onChange={(e) => onInputChange('equipment_needed', e.target.value.split(',').map(s => s.trim()))}
              placeholder="Enter equipment separated by commas"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Maximum Group Size</Label>
              <Input
                type="number"
                value={place.max_group_size || ''}
                onChange={(e) => onInputChange('max_group_size', parseInt(e.target.value))}
                placeholder="Max participants"
              />
            </div>

            <div className="space-y-2">
              <Label>Minimum Age</Label>
              <Input
                type="number"
                value={place.min_age || ''}
                onChange={(e) => onInputChange('min_age', parseInt(e.target.value))}
                placeholder="Minimum age"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              checked={place.suitable_for_children}
              onCheckedChange={(checked) => onInputChange('suitable_for_children', checked)}
            />
            <Label>Suitable for Children</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              checked={place.weather_dependent}
              onCheckedChange={(checked) => onInputChange('weather_dependent', checked)}
            />
            <Label>Weather Dependent</Label>
          </div>
        </>
      ) : (
        <div className="space-y-4">
          {place.difficulty_level && (
            <Badge 
              variant="outline" 
              className={cn(
                "capitalize",
                place.difficulty_level === 'easy' && "bg-green-50 text-green-700",
                place.difficulty_level === 'moderate' && "bg-yellow-50 text-yellow-700",
                place.difficulty_level === 'challenging' && "bg-orange-50 text-orange-700",
                place.difficulty_level === 'expert' && "bg-red-50 text-red-700"
              )}
            >
              {place.difficulty_level}
            </Badge>
          )}

          {place.duration && (
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span>{place.duration}</span>
            </div>
          )}

          {place.equipment_needed?.length > 0 && (
            <div className="space-y-2">
              <span className="text-sm text-muted-foreground">Equipment needed:</span>
              <div className="flex flex-wrap gap-2">
                {place.equipment_needed.map((equipment: string) => (
                  <Badge key={equipment} variant="secondary">
                    {equipment}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {place.max_group_size && (
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span>Max group: {place.max_group_size}</span>
              </div>
            )}

            {place.min_age && (
              <div className="flex items-center gap-2">
                <Baby className="w-4 h-4 text-muted-foreground" />
                <span>Min age: {place.min_age}</span>
              </div>
            )}
          </div>

          {place.suitable_for_children && (
            <Badge variant="outline" className="bg-green-50 text-green-700">
              <Baby className="w-4 h-4 mr-1" />
              Kid-friendly
            </Badge>
          )}

          {place.weather_dependent && (
            <Badge variant="outline" className="bg-blue-50 text-blue-700">
              <Sun className="w-4 h-4 mr-1" />
              Weather dependent
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
